/*
Copyright (C) 2023-2026 QuantumNous

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

For commercial licensing, please contact support@quantumnous.com
*/
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { SubscriptionsDialogs } from '@/features/subscriptions/components/subscriptions-dialogs'
import { SubscriptionsPrimaryButtons } from '@/features/subscriptions/components/subscriptions-primary-buttons'
import { SubscriptionsProvider } from '@/features/subscriptions/components/subscriptions-provider'
import { SubscriptionsTable } from '@/features/subscriptions/components/subscriptions-table'
import { useUpdateOption } from '../hooks/use-update-option'
import type { BillingSettings } from '../types'

interface SubscriptionsOverviewSectionProps {
  settings: BillingSettings
}

// One row of the Flow generation pricing table (FlowCostItems option).
interface FlowCostItem {
  sku: string
  label: string
  points: number
}

function parseFlowCostItems(raw: unknown): FlowCostItem[] {
  if (typeof raw !== 'string' || !raw.trim()) return []
  try {
    const arr = JSON.parse(raw)
    if (!Array.isArray(arr)) return []
    return arr.map((x) => ({
      sku: String(x?.sku ?? ''),
      label: String(x?.label ?? ''),
      points: Number(x?.points ?? 0),
    }))
  } catch {
    return []
  }
}

export function SubscriptionsOverviewSection({
  settings,
}: SubscriptionsOverviewSectionProps) {
  const { t } = useTranslation()
  const updateOption = useUpdateOption()
  const [pointPerUsd, setPointPerUsd] = useState<number>(
    settings.SubscriptionPointPerUsd ?? 100
  )
  const [costItems, setCostItems] = useState<FlowCostItem[]>(() =>
    parseFlowCostItems(settings.FlowCostItems)
  )
  const [defaultCost, setDefaultCost] = useState<number>(
    settings.FlowDefaultCost ?? 1
  )

  const updateCostItem = (i: number, patch: Partial<FlowCostItem>) =>
    setCostItems((prev) => prev.map((it, idx) => (idx === i ? { ...it, ...patch } : it)))
  const addCostItem = () =>
    setCostItems((prev) => [...prev, { sku: '', label: '', points: 1 }])
  const removeCostItem = (i: number) =>
    setCostItems((prev) => prev.filter((_, idx) => idx !== i))

  const handleSavePointPerUsd = async () => {
    try {
      await updateOption.mutateAsync({
        key: 'SubscriptionPointPerUsd',
        value: String(pointPerUsd),
      })
      toast.success(t('Saved successfully'))
    } catch {
      toast.error(t('Save failed'))
    }
  }

  const handleSaveFlowCost = async () => {
    try {
      const cleaned = costItems
        .map((it) => ({
          sku: it.sku.trim(),
          label: it.label.trim(),
          points: Math.max(1, Math.round(it.points || 0)),
        }))
        .filter((it) => it.sku)
      await updateOption.mutateAsync({
        key: 'FlowCostItems',
        value: JSON.stringify(cleaned),
      })
      await updateOption.mutateAsync({
        key: 'FlowDefaultCost',
        value: String(Math.max(1, Math.round(defaultCost))),
      })
      toast.success(t('Saved successfully'))
    } catch {
      toast.error(t('Save failed'))
    }
  }

  return (
    <div className='space-y-4 sm:space-y-6'>
      {/* 1) Plans management — reuses /console/subscriptions components */}
      <Card>
        <CardContent className='space-y-4 p-4 sm:p-6'>
          <div className='flex flex-wrap items-start justify-between gap-3'>
            <div className='space-y-1'>
              <h3 className='text-base font-semibold'>{t('Subscription Plans')}</h3>
              <p className='text-sm text-muted-foreground'>
                {t('Create, edit, enable/disable subscription tiers (Plus / Pro / Max)')}
              </p>
            </div>
          </div>
          <SubscriptionsProvider>
            <div className='flex justify-end'>
              <SubscriptionsPrimaryButtons />
            </div>
            <SubscriptionsTable />
            <SubscriptionsDialogs />
          </SubscriptionsProvider>
        </CardContent>
      </Card>

      {/* 1b) Subscription point-per-USD exchange rate */}
      <Card>
        <CardContent className='space-y-3 p-4 sm:p-6'>
          <div className='space-y-1 pb-2'>
            <h3 className='text-base font-semibold'>
              {t('Subscription Points Exchange Rate')}
            </h3>
            <p className='text-sm text-muted-foreground'>
              {t(
                'How many points equal 1 USD for subscription users. Used to display quota as points in the user profile.'
              )}
            </p>
          </div>
          <div className='flex items-center gap-3'>
            <span className='text-sm text-muted-foreground whitespace-nowrap'>
              {t('1 USD = N Points')}
            </span>
            <Input
              type='number'
              min={0}
              step={0.01}
              value={pointPerUsd}
              onChange={(e) => setPointPerUsd(parseFloat(e.target.value) || 0)}
              className='w-36'
            />
            <Button
              size='sm'
              onClick={handleSavePointPerUsd}
              disabled={updateOption.isPending}
            >
              {updateOption.isPending ? t('Saving...') : t('Save')}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* 1c) Flow generation cost — editable per-sku points table (add/remove) */}
      <Card>
        <CardContent className='space-y-3 p-4 sm:p-6'>
          <div className='space-y-1 pb-2'>
            <h3 className='text-base font-semibold'>
              {t('Flow Generation Cost')}
            </h3>
            <p className='text-sm text-muted-foreground'>
              {t(
                'Points deducted per Flow generation, by sku (type/size). Deducted from the subscription points ledger (not the API wallet). Add one row per generation type — the observer reports a matching sku.'
              )}
            </p>
          </div>
          <div className='space-y-2'>
            <div className='flex items-center gap-2 text-xs text-muted-foreground'>
              <span className='w-44'>{t('Sku')}</span>
              <span className='flex-1'>{t('Label')}</span>
              <span className='w-24'>{t('Points')}</span>
              <span className='w-8' />
            </div>
            {costItems.map((it, i) => (
              <div key={i} className='flex items-center gap-2'>
                <Input
                  className='w-44'
                  placeholder='video_1080'
                  value={it.sku}
                  onChange={(e) => updateCostItem(i, { sku: e.target.value })}
                />
                <Input
                  className='flex-1'
                  placeholder={t('Label')}
                  value={it.label}
                  onChange={(e) => updateCostItem(i, { label: e.target.value })}
                />
                <Input
                  className='w-24'
                  type='number'
                  min={1}
                  step={1}
                  value={it.points}
                  onChange={(e) => updateCostItem(i, { points: parseInt(e.target.value) || 0 })}
                />
                <Button
                  variant='ghost'
                  size='sm'
                  className='h-8 w-8 p-0 text-destructive'
                  onClick={() => removeCostItem(i)}
                  title={t('Delete')}
                >
                  ✕
                </Button>
              </div>
            ))}
            <Button variant='outline' size='sm' onClick={addCostItem}>
              + {t('Add Row')}
            </Button>
          </div>
          <div className='flex items-center gap-3 border-t pt-3'>
            <span className='text-sm text-muted-foreground whitespace-nowrap'>
              {t('Default (unknown sku)')}
            </span>
            <Input
              type='number'
              min={1}
              step={1}
              value={defaultCost}
              onChange={(e) => setDefaultCost(parseInt(e.target.value) || 0)}
              className='w-24'
            />
            <Button
              size='sm'
              onClick={handleSaveFlowCost}
              disabled={updateOption.isPending}
            >
              {updateOption.isPending ? t('Saving...') : t('Save')}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Reference — model/group pricing keep their own dedicated pages */}
      <div className='rounded-md border border-warning/20 bg-warning/10 p-3 text-xs text-warning-foreground'>
        {t(
          'Model pricing / Group multipliers / Currency Display each have their own full pages — find them under "Billing & Payment" in the left sidebar (Model Pricing, Group Pricing, Currency Display). New per-call modules (PPT / Embedding / others) plug into the point system by adding one line to the ModelPrice JSON.'
        )}
      </div>
    </div>
  )
}
